import React, { useState, useEffect } from 'react';
import { getFuseList } from '../../api/reports';
import PrintButton from '../common/PrintButton';
import useSwitchboardPicker, { SwitchboardSelect } from './useSwitchboardPicker';
import './Reports.css';

const PRINT_ROLES = ['admin', 'engineer', 'manager', 'foreman', 'team-leader'];

// Incoming and bus-tie breakers are named by their function
const consumerLabel = (r) => (r.consumer ? [r.consumer.tag, r.consumer.description].filter(Boolean).join(' ') : r.roleLabel || 'Spare');

// Breaker fields are only shown on the first row of each breaker
const breakerCell = (r, f) => (r.firstOfBreaker ? r.breaker[f] : '');

function FuseList({ project, role, onOpenTab }) {
  const picker = useSwitchboardPicker(project);
  const sb = picker.selected;
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sb) { setRows([]); return; }
    setLoading(true);
    getFuseList(sb._id)
      .then(r => setRows(r.data.map((row, i, all) => ({
        ...row, firstOfBreaker: i === 0 || all[i - 1].breaker._id !== row.breaker._id,
      }))))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [sb?._id]);

  const breakerCount = rows.filter(r => r.firstOfBreaker).length;

  return (
    <div className="module-wrap">
      <div className="module-toolbar report-toolbar">
        <span className="module-toolbar-title">Fuse List</span>
        <SwitchboardSelect {...picker} />
        <span className="report-count">{breakerCount} breakers</span>
        {PRINT_ROLES.includes(role) && sb && (
          <PrintButton
            title={`Fuse List — ${sb.name} — ${project?.name ?? ''}`}
            storageKey="fuse-list"
            rows={rows}
            onOpenTab={onOpenTab}
            columns={[
              { key: 'breaker',  label: 'Breaker',      render: r => breakerCell(r, 'tag') },
              { key: 'type',     label: 'Breaker type', render: r => breakerCell(r, 'type') },
              { key: 'setting',  label: 'Setpoint',     render: r => breakerCell(r, 'setting') },
              { key: 'consumer', label: 'Consumer',     render: r => consumerLabel(r) },
              { key: 'cable',    label: 'Cable',        render: r => r.cable?.tag || '' },
              { key: 'cableType', label: 'Cable type',  render: r => r.cable?.type || '' },
              { key: 'location', label: 'Location',     render: r => r.consumer?.location || '' },
            ]}
          />
        )}
      </div>
      <div className="module-body">
        {!sb ? (
          <div className="empty-state">Add switchboards and breakers under Reference Tables first.</div>
        ) : (
          <table className="data-table report-table">
            <thead>
              <tr>
                <th>Breaker</th><th>Breaker type</th><th>Setpoint</th>
                <th>Consumer</th><th>Cable</th><th>Cable type</th><th>Location</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={r.firstOfBreaker ? 'report-group-start' : ''}>
                  <td className="report-breaker">{breakerCell(r, 'tag')}</td>
                  <td>{breakerCell(r, 'type')}</td>
                  <td>{breakerCell(r, 'setting')}</td>
                  <td className={r.consumer ? '' : r.role ? 'report-role' : 'report-muted'}>{consumerLabel(r)}</td>
                  <td>{r.cable?.tag || ''}</td>
                  <td>{r.cable?.type || ''}</td>
                  <td>{r.consumer?.location || ''}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="report-muted">No breakers on this switchboard.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FuseList;
